import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import type { RetroactiveDischargeRunItem } from 'services/api/admin/pccRetroactiveDischargeRuns';
import { BodySmall } from 'styles/typography';

type Props = {
  clientName: string;
  // When the preview whose details the run will use was taken
  previewTakenAt: string;
  pageSize: number;
  items: RetroactiveDischargeRunItem[];
  // Approvable stays the admin unchecked; named so nobody is discharged or left out by a count alone
  leftForStaff: RetroactiveDischargeRunItem[];
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export default function ConfirmDischargeModal({
  clientName,
  previewTakenAt,
  pageSize,
  items,
  leftForStaff,
  isPending,
  onConfirm,
  onClose,
}: Props) {
  const one = items.length === 1 ? items[0] : null;
  const count = items.length;
  const takenAt = simpleDateAtTime(previewTakenAt);
  const title = one ? 'Discharge one patient?' : `Discharge ${count} patients?`;

  return (
    <StandardModal title={title} onCancel={onClose}>
      <StandardModal.RecordName>
        {one ? `Patient: ${one.patient.name} · ${one.facility.name}` : `Client: ${clientName}`}
      </StandardModal.RecordName>
      <Content>
        {one ? (
          <>
            Olio will discharge this stay using the PointClickCare discharge from{' '}
            {simpleDate(one.dischargeDate) ?? 'an unknown date'}
            {one.dispositionText ? ` (${one.dispositionText})` : ''}, as saved by the preview taken {takenAt}, add a
            retroactive note to the Patient Story, and send no emails. The rest of the list is not affected.
          </>
        ) : (
          <>
            Olio will discharge the {count} checked patients in waves of {pageSize}, using the PointClickCare details
            saved by the preview taken {takenAt}, with the same rules as a live auto discharge and no emails. You can
            pause between waves. Skipped patients stay as they are.
          </>
        )}
        {leftForStaff.length > 0 && (
          <LeftForStaff data-testid='left-for-staff'>
            <LeftForStaffTitle>Left for staff ({leftForStaff.length}): not discharged by this run</LeftForStaffTitle>
            <ul>
              {leftForStaff.map((item) => (
                <li key={item.id}>
                  {item.patient.name} · {item.facility.name}
                </li>
              ))}
            </ul>
          </LeftForStaff>
        )}
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={onConfirm} loading={isPending} disabled={isPending} data-testid='confirm-discharge'>
          {one ? 'Discharge patient' : `Discharge ${count} patients`}
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;

const LeftForStaff = styled.div`
  margin-top: 16px;
  max-height: 160px;
  overflow-y: auto;

  ul {
    margin: 4px 0 0;
    padding-left: 20px;
  }
`;

const LeftForStaffTitle = styled.div`
  font-weight: 500;
`;
